import React, { useContext, useEffect, useState, useRef } from 'react';

import IconFlag from '@digital-hig/icon/icons/flag';
import SvgIcon from '@digital-hig/mui/SvgIcon';

import { useKhorosFlagReply } from '../../../apis/khoros/hooks';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import extractErrorMessage from '../../../helpers/extractErrorMessage';
import {
  Tooltip,
  TooltipContent,
  TooltipInfoContent,
  TooltipSuccessContent,
  TooltipTrigger,
} from '../tooltip/Tooltip';

interface CommentProps {
  baseURL?: string;
  commentId: string;
  body: string;
}

export default function FlagButton({ baseURL, commentId, body }: CommentProps) {
  const { KHOROS_API } = useContext(ConfigContext);
  const { userProfile, isLoggedIn } = useContext(LoginContext);
  const { t } = useContext(I18nContext);

  const sub = userProfile?.sub || '';

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isFlagged, setIsFlagged] = useState<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    mutateAsync: onFlagAction,
    isPending: isLoading,
    isError,
    error: flagError,
    reset,
  } = useKhorosFlagReply({
    commentId,
    body,
    baseURL: baseURL || KHOROS_API,
  });

  useEffect(() => {
    const handleStorage = () => {
      const prev = localStorage.getItem('userFlagged');
      const userFlagged = prev ? JSON.parse(prev) : {};
      if (sub && userFlagged[sub] && userFlagged[sub].includes(commentId)) {
        setIsFlagged(true);
      }
    };
    window.addEventListener('storage', handleStorage);
    handleStorage();

    return () => {
      window.removeEventListener('storage', handleStorage);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isError) {
      setError(
        extractErrorMessage(flagError) ||
          t('comments.flagErrorGeneral', { ns: 'common' })
      );
    } else {
      setError('');
    }
  }, [isError]);

  const flagActionHandler = async () => {
    // Reset current tooltips before processing starts.
    setError('');
    setSuccess('');

    // Show error if user is not logged in.
    if (!isLoggedIn) {
      setError(t('error_401', { ns: 'common' }));
      return;
    }

    // Show error if user attempts to repeatedly flag the same comment.
    if (isFlagged) {
      setError(t('comments.flagErrorDuplicate', { ns: 'common' }));
      return;
    }

    try {
      // Optimistically update the UI and start applying the flag on the server.
      setIsFlagged(true);
      await onFlagAction();
      // Update localStorage to prevent user from flagging the same comment again.
      const prev = localStorage.getItem('userFlagged');
      const userFlagged = prev ? JSON.parse(prev) : {};
      if (!userFlagged[sub]) {
        userFlagged[sub] = [commentId];
      } else if (!userFlagged[sub].includes(commentId)) {
        userFlagged[sub] = [...userFlagged[sub], commentId];
      }
      localStorage.setItem('userFlagged', JSON.stringify(userFlagged));
      // Show a temporary success message tooltip.
      setSuccess(t('comments.flagSuccess', { ns: 'common' }));
      timeoutRef.current = setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(t('comments.flagErrorGeneral', { ns: 'common' }));
      setIsFlagged(false);
    }
  };

  const errorCloseHandler = () => {
    setSuccess('');
    if (isError) {
      reset();
    } else {
      setError('');
    }
  };

  return (
    <Tooltip open={!!error || !!success} onOpenChange={errorCloseHandler}>
      <TooltipTrigger
        type="button"
        className="au-inline-block au-align-middle au-ml-c10"
        disabled={isLoading}
        onClick={flagActionHandler}
      >
        <SvgIcon fontsize="large">
          {isFlagged ? (
            <IconFlag fill={isLoading ? 'gray' : 'red'} width={15} />
          ) : (
            <IconFlag fill="gray" width={15} />
          )}{' '}
        </SvgIcon>
      </TooltipTrigger>
      <TooltipContent>
        {error ? <TooltipInfoContent>{error}</TooltipInfoContent> : null}
        {success ? (
          <TooltipSuccessContent>{success}</TooltipSuccessContent>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}
